import { Component, inject, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { SpinnerComponent } from 'shared';

import { DomComponent, DomService, PopupService } from '../../../services';
import { BasePopupComponent } from '../base-popup/base-popup.component';

@Component({
    selector: 'app-file-share-popup',
    templateUrl: './file-share-popup.component.html',
    styleUrl: './file-share-popup.component.scss',
    imports: [SpinnerComponent, TranslatePipe],
})
export class FileSharePopupComponent extends BasePopupComponent<FileSharePopupComponent> {
    private readonly popupService = inject(PopupService);
    private readonly translate = inject(TranslateService);

    protected file = signal<File | null>(null);
    public fileType?: 'UNKNOWN' | 'PDF';
    public fileName?: string;
    public shareTitle?: string;
    public shareText?: string;
    public shareTextNoFileSharing?: string;
    public getFile?: () => Promise<File>;

    public constructor() {
        super();
        this.popupOptions.padding = 0;
    }

    protected async fetchFile(): Promise<void> {
        if (!this.getFile) return;
        try {
            const file = await this.getFile();
            this.file.set(file);
            if (!this.canShare) {
                await this.download();
                this.close();
            }
        } catch (e) {
            this.close();
            this.popupService.showError(
                'comp.file-share-popup.downloadError.title',
                'comp.file-share-popup.downloadError.message',
                'FILE_FETCH_FAILED'
            );
            return;
        }
    }

    protected get fileIcon(): string {
        switch (this.fileType) {
            case 'PDF': {
                return 'picture_as_pdf';
            }
            case 'UNKNOWN':
            default: {
                return 'file_save';
            }
        }
    }

    protected get canShare(): boolean {
        return !!navigator.share && !!navigator.canShare;
    }

    protected async download(): Promise<void> {
        if (!this.file()) return;
        saveAs(this.file(), this.file().name);
    }

    protected async share(): Promise<void> {
        if (!this.file() || !this.canShare) return;
        const shares = [
            {
                title: this.translate.instant(this.shareTitle),
                text: this.translate.instant(this.shareText),
                files: [this.file()],
            },
            {
                title: this.translate.instant(this.shareTitle),
                text: this.translate.instant(this.shareTextNoFileSharing),
            },
        ];
        for (const shareData of shares) {
            if (!navigator.canShare(shareData)) continue;
            try {
                await navigator.share(shareData);
                return;
            } catch (e) {
                console.warn('Failed to call navigator.share with data', shareData, e);
            }
        }
    }

    public static showPopup(
        domService: DomService,
        getFile: () => Promise<File>,
        fileName: string,
        shareTitle: string,
        shareText: string,
        shareTextNoFileSharing: string,
        fileType: 'UNKNOWN' | 'PDF' = 'UNKNOWN'
    ): DomComponent<FileSharePopupComponent> {
        const popup = domService.appendComponentTo<FileSharePopupComponent>(
            'overlay-container',
            FileSharePopupComponent,
            {
                getFile,
                fileName,
                fileType,
                shareTitle,
                shareText,
                shareTextNoFileSharing,
            }
        );
        popup.component.fetchFile();
        return popup;
    }
}
